/**
 * The reseller permission catalog — TypeScript copy of crm-api's
 * `ResellerPermissions` (spec F §2–§3).
 *
 * crm-api is the source of truth and ENFORCES from its own constant on every
 * request. This copy exists so the UI can be typed and rendered without a
 * network round trip; `cypress/e2e/permission_catalog.cy.ts` fetches crm-api's
 * catalog and deep-equals it to these values, so the two cannot drift silently.
 *
 * Nothing here is a gate. `effectiveResellerPermissions` mirrors the server's
 * formula so the Permissions drawer can preview a change live; the server
 * recomputes it from the live membership when the change is saved.
 */

export const RESELLER_ROLES = ["owner", "salesperson", "technician", "viewer"] as const;
export type ResellerRole = (typeof RESELLER_ROLES)[number];

export const EDITABLE_RESELLER_ROLES = ["salesperson", "technician", "viewer"] as const;
export type EditableResellerRole = (typeof EDITABLE_RESELLER_ROLES)[number];

export const RESELLER_ROLE_LABELS: Record<ResellerRole, string> = {
  owner: "Owner",
  salesperson: "Salesperson",
  technician: "Technician",
  viewer: "Viewer",
};

/**
 * CUSTOM ROLES (2026-09-15). An owner may add roles of its own beside the
 * four built-ins. crm-api stamps each one a key of this shape — `custom_` plus
 * a slug of the name it was created with — and the key is STABLE ACROSS
 * RENAMES, so the display name always comes from the role's `label`, never
 * from the key. (That is also why `roleLabel` does not humanise the slug: after
 * a rename it would name the role by what it used to be called.)
 *
 * crm-api validates a custom key against the company on every write; this
 * pattern only lets the BFF refuse a malformed one before a round trip.
 */
export const CUSTOM_ROLE_KEY = /^custom_[a-z0-9_]{1,50}$/;
export type CustomRoleKey = `custom_${string}`;
/** Any role a membership can hold: a built-in, or one of the company's own. */
export type ResellerRoleKey = ResellerRole | CustomRoleKey;

/** The label shown when neither the server nor the built-in table names a role. */
export const UNKNOWN_ROLE_LABEL = "Team member";

export function isBuiltInRole(value: unknown): value is ResellerRole {
  return typeof value === "string" && (RESELLER_ROLES as readonly string[]).includes(value);
}

export function isEditableBuiltInRole(value: unknown): value is EditableResellerRole {
  return typeof value === "string" && (EDITABLE_RESELLER_ROLES as readonly string[]).includes(value);
}

export function isCustomRoleKey(value: unknown): value is CustomRoleKey {
  return typeof value === "string" && CUSTOM_ROLE_KEY.test(value);
}

export function isResellerRoleKey(value: unknown): value is ResellerRoleKey {
  return isBuiltInRole(value) || isCustomRoleKey(value);
}

/** Anything a label can be read from: the server's own label for this role,
 * or a role matrix carrying labels for every role. */
export type RoleLabelSource =
  | string
  | null
  | undefined
  | { roles: Record<string, { label?: string | null } | undefined> };

/**
 * The human name of a role. Prefers what the server said (a label on the
 * row, the profile or the matrix), then the built-in table, then
 * {@link UNKNOWN_ROLE_LABEL} — never the raw key.
 */
export function roleLabel(role: string, source?: RoleLabelSource): string {
  const given =
    typeof source === "string"
      ? source
      : source && typeof source === "object"
        ? source.roles[role]?.label
        : undefined;
  if (typeof given === "string" && given.trim() !== "") return given;
  return isBuiltInRole(role) ? RESELLER_ROLE_LABELS[role] : UNKNOWN_ROLE_LABEL;
}

/**
 * The roles a member can be given, in the order a picker lists them: Owner,
 * then the matrix's own `order` (built-ins and the company's custom roles).
 * Without a matrix — not loaded yet, or refused — the four built-ins.
 */
export function assignableRoles(matrix?: {
  roles: Record<string, { label?: string | null } | undefined>;
  order: readonly string[];
} | null): Array<{ key: ResellerRoleKey; label: string }> {
  const keys: string[] = ["owner", ...(matrix ? matrix.order : EDITABLE_RESELLER_ROLES)];
  return [...new Set(keys)]
    .filter(isResellerRoleKey)
    .map((key) => ({ key, label: roleLabel(key, matrix ?? undefined) }));
}

export const PERMISSION_SECTIONS = {
  customers: "Customers",
  contacts: "Contacts",
  prospects: "Prospects",
  invoices: "Invoices & quotes",
  licences: "Licences",
  documentation: "Documentation",
  // `settings` was a section until 2026-09-14: the portal's Settings page is
  // gone (a reseller brands nothing now), and the keys went with it.
  /** LEGAL — the company's OWN paperwork (2026-09-13; labelled "Legal" since
   * 2026-09-17): the Reseller Agreement + NDA it signed and XOP countersigned,
   * and the company file cabinet. READING it needs no grant — the portal's
   * Legal page is visible to every active member, even when nothing has been
   * filed — so there is no `documents:view` key. `documents:edit` files into
   * the cabinet; no editable role holds it by default, the owner grants it. */
  documents: "Legal",
} as const;
export type PermissionSection = keyof typeof PERMISSION_SECTIONS;

export const PERMISSION_VERBS = ["view", "edit"] as const;
export type PermissionVerb = (typeof PERMISSION_VERBS)[number];

/** Section × verb keys that do NOT exist. `documents:view` was removed on
 * 2026-09-17: the Legal page is readable by every active member of the
 * company, so a read grant for it would gate nothing. Mirrors crm-api's
 * `ALL = SECTIONS × VERBS - %w[documents:view]`. */
const ABSENT_PERMISSIONS = ["documents:view"] as const;

export type ResellerPermission = Exclude<
  `${PermissionSection}:${PermissionVerb}`,
  (typeof ABSENT_PERMISSIONS)[number]
>;

const SECTION_KEYS = Object.keys(PERMISSION_SECTIONS) as PermissionSection[];

/** Catalog order — every list this module returns is sorted by it. */
export const ALL_PERMISSIONS: readonly ResellerPermission[] = SECTION_KEYS.flatMap((s) =>
  PERMISSION_VERBS.map((v) => `${s}:${v}` as const),
).filter((k): k is ResellerPermission => !(ABSENT_PERMISSIONS as readonly string[]).includes(k));

export const UNGRANTABLE_PERMISSIONS: readonly ResellerPermission[] = ["documentation:edit"];

export const GRANTABLE_PERMISSIONS: readonly ResellerPermission[] = ALL_PERMISSIONS.filter(
  (k) => !UNGRANTABLE_PERMISSIONS.includes(k),
);

const ALL_SET = new Set<string>(ALL_PERMISSIONS);

export function isResellerPermission(value: unknown): value is ResellerPermission {
  return typeof value === "string" && ALL_SET.has(value);
}

/** Cross-section implications (spec F §2.1); `edit ⇒ view` is generic. */
const CROSS_IMPLICATIONS: Partial<Record<ResellerPermission, ResellerPermission[]>> = {
  "licences:view": ["customers:view"],
  "invoices:edit": ["customers:view"],
};

/** Every implication, including the generic ones — the same map crm-api's
 * `implications_json` serves, so "implied by …" can be shown without
 * re-deriving the rule. */
export const PERMISSION_IMPLICATIONS: Readonly<Record<string, ResellerPermission[]>> = Object.fromEntries(
  ALL_PERMISSIONS.flatMap((key) => {
    const [section, verb] = key.split(":") as [PermissionSection, PermissionVerb];
    const implied: ResellerPermission[] = [];
    const sectionView = `${section}:view`;
    // `documents:edit` implies no read key — reading Legal is ungated.
    if (verb === "edit" && isResellerPermission(sectionView)) implied.push(sectionView);
    implied.push(...(CROSS_IMPLICATIONS[key] ?? []));
    return implied.length > 0 ? [[key, implied]] : [];
  }),
);

export const DEFAULT_ROLE_PERMISSIONS: Record<EditableResellerRole, ResellerPermission[]> = {
  salesperson: [
    "customers:view", "customers:edit", "contacts:view", "contacts:edit",
    "prospects:view", "prospects:edit",
    // Salespeople create licences (CEO, 2026-09-17); crm-api keeps trials to a
    // customer's first licence and holds a full one for super admin approval.
    "invoices:view", "invoices:edit", "licences:view", "licences:edit", "documentation:view",
  ],
  technician: ["customers:view", "contacts:view", "licences:view", "licences:edit", "documentation:view"],
  viewer: [
    "customers:view", "contacts:view", "prospects:view", "invoices:view", "licences:view",
    "documentation:view",
  ],
};

/** Close a set under the implication rules; unknown keys are dropped. */
export function closurePermissions(keys: readonly string[]): ResellerPermission[] {
  const set = new Set<ResellerPermission>(keys.filter(isResellerPermission));
  let size = -1;
  while (size !== set.size) {
    size = set.size;
    for (const key of [...set]) for (const implied of PERMISSION_IMPLICATIONS[key] ?? []) set.add(implied);
  }
  return ALL_PERMISSIONS.filter((k) => set.has(k));
}

/** THE formula, mirrored from crm-api (spec F §4.2). */
export function effectiveResellerPermissions(input: {
  role: ResellerRoleKey;
  bundle?: readonly string[] | null;
  grants?: readonly string[];
  denies?: readonly string[];
}): ResellerPermission[] {
  if (input.role === "owner") return [...ALL_PERMISSIONS];
  // A custom role has no shipped default: without its bundle it grants
  // nothing, which is what crm-api enforces for it too.
  const base =
    input.bundle ?? (isEditableBuiltInRole(input.role) ? DEFAULT_ROLE_PERMISSIONS[input.role] : []);
  const denies = new Set(input.denies ?? []);
  const union = [...base, ...(input.grants ?? [])].filter((k) => !denies.has(k));
  return closurePermissions(union);
}

/** Which of `held` force `key` on through the closure — for "implied by …". */
export function impliedBy(key: ResellerPermission, held: readonly string[]): ResellerPermission[] {
  return held
    .filter(isResellerPermission)
    .filter((h) => h !== key && closurePermissions([h]).includes(key));
}
